import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons/faCircleInfo';
import { faStar } from '@fortawesome/pro-solid-svg-icons/faStar';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { FlagProfileModal } from 'us-react-components/dist/FlagProfileModal/FlagProfileModal';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import { ParentSummary } from 'us-react-components/dist/ParentSummary/ParentSummary';
import { RecurringScheduleDisplay } from 'us-react-components/dist/RecurringScheduleDisplay/RecurringScheduleDisplay';
import { formatDateRange, getDurationString } from 'us-react-components/dist/utils/date';
import { bookingVerticalType } from 'us-react-components/dist/constants/Booking';
import {
  axios,
  InterestedSitter,
  InterestedSitterService,
  Job,
  JobService,
  SitterProfileStatus,
  SitterProfileStatusService,
} from 'us-web-services';

import { ApprovalChecklist } from '../../components/Dashboard/ApprovalChecklist';
import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { getJobNavState } from '../JobBoard/jobNavStorage';

const ARTICLE_ASSET_SERVER = 'https://assets.urbansitter.com';

const ARTICLES = [
  {
    id: 'how-to-get-jobs',
    title: 'How to get jobs on UrbanSitter',
    img: `${ARTICLE_ASSET_SERVER}/static/img/backgrounds/sitter-girl-dancing-thumbnail.webp`,
    alt: 'sitter dancing with child',
    url: 'https://blog.urbansitter.com/how-to-get-jobs-on-urbansitter/',
  },
  {
    id: 'getting-started',
    title: 'Getting started on UrbanSitter',
    img: `${ARTICLE_ASSET_SERVER}/static/img/backgrounds/sitter-with-child-thumbnail.webp`,
    alt: 'sitter reading to child',
    url: 'https://blog.urbansitter.com/starting-your-urbansitter-profile-tips-for-caregivers/',
  },
  {
    id: 'how-to-land-nanny-jobs',
    title: 'How to land nanny jobs',
    img: `${ARTICLE_ASSET_SERVER}/static/img/backgrounds/paddy-cake-thumbnail.webp`,
    alt: 'sitter and child playing paddy cake',
    url: 'https://blog.urbansitter.com/land-nanny-jobs/',
  },
  {
    id: 'how-to-get-recommendations',
    title: 'How to get recommendations',
    img: `${ARTICLE_ASSET_SERVER}/static/img/backgrounds/thumbs-up-thumbnail.webp`,
    alt: 'thumbs up',
    url: 'https://support.urbansitter.com/hc/en-us/articles/216952707',
  },
];

type JobQuestion = { id: number; text: string };
type InterestedSitterExtended = InterestedSitter & {
  message?: string;
  parentViewedApplication?: boolean;
  answers?: Array<{ text: string; question: { id: number } }>;
};

type ParentExtended = {
  id?: number;
  firstName?: string;
  lastName?: string;
  pictureUrl?: string;
  userStats?: { lastAccessLabel?: string };
  affiliations?: Array<{ id?: number; affiliation?: { name?: string } }>;
  partnerBadge?: { label?: string; type?: string };
  covidBadge?: { consented?: boolean };
  badges?: Array<{ type?: string; label?: string }>;
  relationship?: {
    connections?: {
      indirect?: Array<{ id?: number; name?: string; pictureUrl?: string }>;
      affiliation?: Array<{ id?: number; name?: string; memberCount?: number; pictureUrl?: string }>;
      company?: Array<{ id?: number; name?: string; memberCount?: number; pictureUrl?: string }>;
      school?: Array<{ id?: number; name?: string; memberCount?: number; pictureUrl?: string }>;
      localGroup?: Array<{ id?: number; name?: string; memberCount?: number; pictureUrl?: string }>;
    };
  };
};

const VERTICAL_LABEL_KEYS: Record<number, string> = {
  [bookingVerticalType.CHILD_CARE]: 'childCare',
  [bookingVerticalType.PET_CARE]: 'petCare',
  [bookingVerticalType.SENIOR_CARE]: 'seniorCare',
  [bookingVerticalType.HOUSEHOLD_SERVICES]: 'householdServices',
  [bookingVerticalType.DAY_CARE]: 'dayCare',
};

const paymentLabel = (type?: string): string => {
  if (!type) return '';
  if (type === 'credit') return 'credit card';
  if (type === 'cash') return 'cash';
  return type;
};

const formatJobDateRange = (start?: string, end?: string): string =>
  formatDateRange(start, end, 'long');

const timeFromNow = (dateVal?: Date | string): string => {
  if (!dateVal) return '';
  const raw = typeof dateVal === 'string' ? dateVal.replace(' ', 'T') : dateVal.toISOString();
  const d = new Date(raw);
  if (isNaN(d.getTime())) return '';
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} ${months === 1 ? 'month' : 'months'} ago`;
  const years = Math.floor(months / 12);
  return `${years} ${years === 1 ? 'year' : 'years'} ago`;
};

interface JobWithInterest extends Job {
  sitterInterest?: InterestedSitterExtended;
  questions?: JobQuestion[];
}

type ApplyErrorCode =
  | 'interested_sitter.direct_deposit'
  | 'interested_sitter.already_booked'
  | 'interested_sitter.not_available'
  | 'interested_sitter.payout_pending'
  | 'interested_sitter.payout_setup'
  | 'interested_sitter.not_accepting_payment'
  | 'interested_sitter.payout_deactivated'
  | 'interested_sitter.payout_disabled'
  | 'interested_sitter.availability.part_time'
  | 'interested_sitter.availability.full_time'
  | 'interested_sitter.availability.occasional'
  | 'interested_sitter.contact_us'
  | 'interested_sitter.incomplete_profile'
  | 'interested_sitter.pending_review'
  | string;

type ApplyErrorButton = { text: string; onClick: () => void };
type ApplyErrorModalState = {
  header: string;
  title?: string;
  body?: string;
  primary?: ApplyErrorButton;
  secondary?: ApplyErrorButton;
};

export const JobDetail = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores } = useDependencies();
  const user = stores.user.user;

  const [job, setJob] = useState<JobWithInterest | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [jobNotifications, setJobNotifications] = useState(true);
  const [similarJobs, setSimilarJobs] = useState<Job[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [modalStep, setModalStep] = useState<'questions' | 'message'>('message');
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [interestMessage, setInterestMessage] = useState('');
  const [jobIsHidden, setJobIsHidden] = useState<boolean>(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [approvalChecklistOpen, setApprovalChecklistOpen] = useState(false);
  const [profileStatus, setProfileStatus] = useState<SitterProfileStatus | null>(null);
  const [errorModal, setErrorModal] = useState<ApplyErrorModalState | null>(null);
  const [passedErrors, setPassedErrors] = useState<string[]>([]);
  const [navState] = useState(() => getJobNavState());

  useEffect(() => {
    document.title = 'UrbanSitter - Job Detail';
  }, []);

  const jobIndex = navState && job?.id ? navState.ids.indexOf(job.id) : -1;
  const fromSearch = (navState?.ids.length ?? 0) > 0;
  const prevJobId = jobIndex > 0 ? navState!.ids[jobIndex - 1] : null;
  const nextJobId =
    jobIndex >= 0 && jobIndex < (navState?.ids.length ?? 0) - 1
      ? navState!.ids[jobIndex + 1]
      : null;
  const searchText = fromSearch
    ? t('pages.jobDetail.returnToSearch')
    : t('pages.jobDetail.searchForMoreJobs');
  const searchUrl = navState?.searchUrl ?? '/job-board';

  const loadJob = async (): Promise<void> => {
    const jobId = Number(pathId);
    if (!jobId || !user?.id) return;
    try {
      const resp = await JobService.findById(jobId, {
        params: { userId: user.id, sitterId: user.id, fields: 'sitterInterest' },
      });
      setJob((resp.data?.data ?? null) as JobWithInterest | null);
    } catch (err) {
      console.warn('[JobDetail] load failed', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadJob();

  }, [pathId, user?.id]);

  useEffect(() => {
    if (!user?.id || !job?.id) return;
    // The /v2/similar-jobs endpoint sometimes returns the array at the body
    // root and sometimes wrapped in `{data: [...]}` — handle both.
    const load = async (): Promise<void> => {
      try {
        const resp: { data?: unknown } = await axios.get(`/v2/similar-jobs/${job.id}`, { params: { sitterId: user.id } });
        const body = resp.data;
        const list = Array.isArray(body)
          ? (body as Job[])
          : ((body as { data?: Job[] } | undefined)?.data ?? []);
        setSimilarJobs(list.filter((j) => j.id !== job.id));
      } catch {
        setSimilarJobs([]);
      }
    };
    void load();
  }, [user?.id, job?.id]);

  useEffect(() => {
    if (!user?.id || !job?.id) return;
    const load = async (): Promise<void> => {
      try {
        const resp: { data: unknown[] } = await axios.get('/v2/hidden-jobs/', { params: { sitterId: user.id, jobId: job.id } });
        setJobIsHidden(Array.isArray(resp.data) && resp.data.length > 0);
      } catch {
        setJobIsHidden(false);
      }
    };
    void load();
  }, [user?.id, job?.id]);

  const isClosed = job?.status?.startsWith('closed_');

  const openExpressInterestModal = (updatedJob?: JobWithInterest): void => {
    const j = updatedJob ?? job;
    const questions = j?.questions ?? [];
    setModalStep(questions.length > 0 ? 'questions' : 'message');
    const existingAnswers: Record<number, string> = {};
    j?.sitterInterest?.answers?.forEach((a) => {
      existingAnswers[a.question.id] = a.text;
    });
    setAnswers(existingAnswers);
    setInterestMessage(j?.sitterInterest?.message ?? '');
    setShowModal(true);
  };

  const handleApplyError = async (
    code: ApplyErrorCode,
    message: string,
    nextPassedErrors: string[],
  ): Promise<void> => {
    if (code === 'interested_sitter.incomplete_profile') {
      if (!user?.id) return;
      try {
        const resp = await SitterProfileStatusService.get(user.id);
        setProfileStatus(resp.data?.data ?? null);
        setApprovalChecklistOpen(true);
      } catch {
        toast.error(t('pages.jobInterest.applyError'));
      }
      return;
    }
    if (code === 'interested_sitter.pending_review') {
      setErrorModal({
        header: "You can't apply to jobs quite yet",
        title: 'Your profile is waiting to be approved',
        body:
          "We're currently handling a high volume of requests, so it could take up to " +
          '7-14 days for your profile to be reviewed.',
      });
      return;
    }
    if (code === 'interested_sitter.contact_us') {
      setErrorModal({
        header: "You can't apply to jobs right now",
        title: 'Your profile is hidden',
        body: 'Please contact support@urbansitter.com with questions.',
      });
      return;
    }

    const payoutHref = `/app/sitter/payout?jobId=${job?.id ?? ''}&fromSearch=true`;
    const retryAfterPassing = (passCode: string): (() => void) => (): void => {
      setErrorModal(null);
      void submitInterest([...nextPassedErrors, passCode]);
    };

    if (code === 'interested_sitter.direct_deposit') {
      setErrorModal({
        header: message,
        body:
          'We all love cash, but this family prefers to pay caregivers with a credit card. ' +
          "Still interested? Set up your payout account now. It's free and easy!",
        primary: {
          text: 'Set up payout account',
          onClick: (): void => { window.location.href = payoutHref; },
        },
      });
      return;
    }
    if (code === 'interested_sitter.payout_setup') {
      setErrorModal({
        header: 'Heads up, this family is paying by credit card',
        body: message,
        primary: {
          text: 'Continue to application',
          onClick: retryAfterPassing('interested_sitter.payout_setup'),
        },
      });
      return;
    }
    if (code === 'interested_sitter.payout_pending') {
      setErrorModal({
        header: 'Heads up, this family is paying by credit card',
        body: message,
        primary: {
          text: 'Apply',
          onClick: retryAfterPassing('interested_sitter.payout_pending'),
        },
      });
      return;
    }
    if (code === 'interested_sitter.not_accepting_payment') {
      setErrorModal({
        header: 'Allow credit card payments?',
        body: message,
        primary: {
          text: 'Allow and apply',
          onClick: retryAfterPassing('interested_sitter.not_accepting_payment'),
        },
        secondary: { text: 'Cancel', onClick: (): void => setErrorModal(null) },
      });
      return;
    }
    if (code === 'interested_sitter.payout_deactivated') {
      setErrorModal({
        header: 'Heads up, this family is paying by credit card',
        body: message,
        primary: { text: 'Cancel', onClick: (): void => setErrorModal(null) },
      });
      return;
    }
    if (code === 'interested_sitter.payout_disabled') {
      setErrorModal({
        header: 'You are not eligible for credit card payments',
        body: message,
        primary: { text: 'OK', onClick: (): void => setErrorModal(null) },
      });
      return;
    }
    if (code === 'interested_sitter.already_booked') {
      setErrorModal({
        header: 'Oops',
        body: message,
        primary: { text: 'Close', onClick: (): void => setErrorModal(null) },
      });
      return;
    }
    if (code === 'interested_sitter.not_available') {
      setErrorModal({
        header: 'Oops',
        body: message,
        primary: {
          text: 'Update my availability',
          onClick: (): void => { window.location.href = '/app/sitter/calendar'; },
        },
      });
      return;
    }
    if (code.indexOf('interested_sitter.availability') === 0) {
      setErrorModal({
        header: message,
        body: 'Help your profile stand out! Update your profile preferences from the Profile page.',
        primary: {
          text: 'Apply anyway',
          onClick: retryAfterPassing(code),
        },
        secondary: { text: 'Cancel', onClick: (): void => setErrorModal(null) },
      });
      return;
    }

    setErrorModal({ header: 'Oops', body: message });
  };

  const submitInterest = async (passed: string[] = passedErrors): Promise<void> => {
    if (!job?.id || !user?.id || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const resp = await InterestedSitterService.create({
        ignoreErrors: passed,
        job: { id: job.id },
        sendNotifications: jobNotifications,
        sitter: { id: user.id },
      });
      const newInterest = resp.data?.data;
      const updatedJob: JobWithInterest = {
        ...job,
        sitterInterest: newInterest as InterestedSitterExtended,
      };
      setJob(updatedJob);
      setPassedErrors([]);
      setErrorModal(null);
      openExpressInterestModal(updatedJob);
    } catch (e) {
      const errResp = (e as { response?: { data?: { errors?: Array<{ code?: string; message?: string }> } } }).response;
      const first = errResp?.data?.errors?.[0];
      if (first?.code) {
        setPassedErrors(passed);
        await handleApplyError(first.code, first.message ?? '', passed);
      } else {
        toast.error(t('pages.jobInterest.applyError'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const apply = (): Promise<void> => submitInterest([]);

  const interestIdentity = (): Partial<InterestedSitter> | null =>
    job?.id && user?.id ? { sitter: { id: user.id }, job: { id: job.id } } : null;

  const reapply = async (): Promise<void> => {
    const identity = interestIdentity();
    if (!job?.sitterInterest || !identity || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const resp = await InterestedSitterService.patch({
        ...identity,
        withdrawn: false,
        sendNotifications: jobNotifications,
      });
      const updatedInterest = resp.data?.data as InterestedSitterExtended;
      const updatedJob: JobWithInterest = { ...job, sitterInterest: updatedInterest };
      setJob(updatedJob);
      openExpressInterestModal(updatedJob);
    } catch {
      toast.error(t('pages.jobInterest.applyError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const withdraw = async (): Promise<void> => {
    const identity = interestIdentity();
    if (!job?.sitterInterest || !identity || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await InterestedSitterService.patch({ ...identity, withdrawn: true });
      await loadJob();
    } catch {
      toast.error(t('pages.jobInterest.withdrawError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendAnswers = async (): Promise<void> => {
    const identity = interestIdentity();
    if (!job?.sitterInterest || !identity) { setModalStep('message'); return; }
    const questions = job?.questions ?? [];
    const answersArray = questions.map((q) => ({ text: answers[q.id] ?? '', question: { id: q.id } }));
    try {
      await InterestedSitterService.patch({ ...identity, answers: answersArray });
    } catch { /* proceed to next step regardless */ }
    setModalStep('message');
  };

  const sendMessage = async (): Promise<void> => {
    const identity = interestIdentity();
    if (job?.sitterInterest && identity) {
      try {
        await InterestedSitterService.patch({ ...identity, message: interestMessage, sendEmail: true });
      } catch { /* ignore */ }
    }
    setShowModal(false);
  };

  const hideJob = async (): Promise<void> => {
    if (!user?.id || !job?.id) return;
    try {
      await axios.post('/v2/hidden-jobs/', { sitter: { id: user.id }, job: { id: job.id } });
      setJobIsHidden(true);
    } catch { /* ignore */ }
  };

  const unhideJob = async (): Promise<void> => {
    if (!user?.id || !job?.id) return;
    try {
      await axios.delete('/v2/hidden-jobs/', { data: { sitterId: user.id, jobId: job.id } });
      setJobIsHidden(false);
    } catch { /* ignore */ }
  };

  if (isLoading) {
    return (
      <div className='container py-8'>
        <i className='fa-solid fa-spinner fa-spin mr-2' />
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  if (!job) return <div className='container py-8' />;

  const booking = job.booking;
  const parent = booking?.parent as ParentExtended | undefined;
  const hasApplied = !!job.sitterInterest && !job.sitterInterest.withdrawn;
  const hasWithdrawn = !!job.sitterInterest?.withdrawn;
  const applicationViewed = !!job.sitterInterest?.parentViewedApplication;
  const typeId = booking?.typeId;
  const verticalLabelKey = typeId != null ? VERTICAL_LABEL_KEYS[typeId] : undefined;
  const verticalLabel = verticalLabelKey ? t(`common.verticalLabels.${verticalLabelKey}`) : undefined;
  const includes = ((booking as { includes?: Array<{ id?: number; label?: string }> } | undefined)?.includes) ?? [];
  const sweeteners =
    ((booking as { sweeteners?: Array<{ id?: number; label?: string }> } | undefined)
      ?.sweeteners) ?? [];
  const minimumHours = (job as { minimumHours?: number }).minimumHours;
  const distanceLabel = job.distance != null
    ? (job.distance <= 1 ? '< 1 mile' : `${job.distance} miles`)
    : '';
  const postedAgo = timeFromNow(job.created);
  const isHouseholdServices = typeId === bookingVerticalType.HOUSEHOLD_SERVICES;

  const parentLastName = parent?.lastName;
  const parentNameDisplay = [parent?.firstName, parentLastName ? parentLastName.charAt(0) : '']
    .filter(Boolean)
    .join(' ');

  return (
    <div className='container py-8'>
      {/* Back to search nav */}
      <div id='sitter-detail-nav' className='mb-4 font-semibold'>
        <Button
          buttonType={ButtonType.tertiary}
          className='text-sapphire font-semibold text-base'
          onClick={(): void => navigate(searchUrl)}
        >
          <i className='fa-solid fa-magnifying-glass mr-2' />
          {searchText}
        </Button>
        {jobIndex >= 0 && (
          <div className='flex items-center justify-between text-sm mt-2'>
            <Button
              buttonType={ButtonType.tertiary}
              disabled={!prevJobId}
              onClick={(): void => { if (prevJobId) navigate(`/job/${prevJobId}`); }}
              className={prevJobId ? '' : 'opacity-40 cursor-default'}
            >
              <i className='fa-solid fa-chevron-left mr-1' /> Previous Job
            </Button>
            <Button
              buttonType={ButtonType.tertiary}
              disabled={!nextJobId}
              onClick={(): void => { if (nextJobId) navigate(`/job/${nextJobId}`); }}
              className={nextJobId ? '' : 'opacity-40 cursor-default'}
            >
              Next Job <i className='fa-solid fa-chevron-right ml-1' />
            </Button>
          </div>
        )}
      </div>

      <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
        {/* ── Left column (2/3): job detail + parent summary ── */}
        <div className='md:col-span-2'>
          <div id='job-detail-section' className='us-card rounded p-6'>
            {/* Header: parent photo + name + heading */}
            <div className='flex items-start gap-4 mb-4'>
              {parent?.pictureUrl && (
                <a href={`/app/parent/${parent.id}`}>
                  <img
                    src={parent.pictureUrl}
                    alt={parent.firstName ?? ''}
                    className='w-20 h-20 rounded-full object-cover flex-shrink-0'
                  />
                </a>
              )}
              <div className='flex-1'>
                {parent?.id && parentNameDisplay && (
                  <a
                    href={`/app/parent/${parent.id}`}
                    className='font-semibold text-base hover:underline'
                  >
                    {parentNameDisplay}
                  </a>
                )}
                <h1 className='text-xl font-semibold mt-1 mb-1'>
                  {job.recurring
                    ? `${job.availability ?? ''}, Starting ${formatJobDateRange(booking?.start)}`
                    : formatJobDateRange(booking?.start, booking?.end)}
                </h1>
                {job.recurring && (
                  <RecurringScheduleDisplay
                    days={job.recurring.days}
                    timeSummary={job.recurring.timeSummary}
                    flexible={job.recurring.flexible}
                    sameTimes={job.recurring.sameTimes}
                    timeOfDay={job.recurring.timeOfDay}
                  />
                )}
                <div className='font-semibold text-base text-neutral-700'>
                  <span className='text-neutral-500 mr-1'>Location:</span>
                  {booking?.address?.city ? (
                    <span>{booking.address.city}</span>
                  ) : (
                    <span className='italic text-neutral-500'>Not specified</span>
                  )}
                  {booking?.address?.neighborhood && (
                    <span> - {booking.address.neighborhood}</span>
                  )}
                  {booking?.address?.state && (
                    <span>, {booking.address.state}</span>
                  )}
                  {booking?.address?.zip && (
                    <span> {booking.address.zip}</span>
                  )}
                  {distanceLabel && (
                    <span className='text-neutral-500'> ({distanceLabel})</span>
                  )}
                </div>
                {!isHouseholdServices && booking?.numberOfChildren != null && (
                  <div className='font-semibold text-base text-neutral-700 mt-1'>
                    {booking.numberOfChildren}{' '}
                    {typeId === bookingVerticalType.CHILD_CARE ? (
                      <>
                        {booking.numberOfChildren === 1 ? 'child' : 'children'}
                        {(booking as { kidsAges?: string })?.kidsAges
                          ? `: ${(booking as { kidsAges?: string }).kidsAges}`
                          : ''}
                      </>
                    ) : typeId === bookingVerticalType.PET_CARE ? (
                      booking.numberOfChildren === 1 ? 'pet' : 'pets'
                    ) : typeId === bookingVerticalType.SENIOR_CARE ? (
                      `${booking.numberOfChildren === 1 ? 'senior' : 'seniors'} receiving care`
                    ) : null}
                  </div>
                )}
                {job.type === 'one_time' && (
                  <div className='font-semibold text-base text-neutral-700 mt-1'>One-time job</div>
                )}
              </div>
            </div>

            <hr className='my-4' />

            {/* Active + Posted timestamps */}
            <div className='text-sm text-neutral-600 mb-3'>
              {parent?.userStats?.lastAccessLabel && (
                <div className='mb-1'>
                  <i className='fa-regular fa-bolt mr-1' />
                  <span className='lowercase font-semibold'>active {parent.userStats.lastAccessLabel}</span>
                </div>
              )}
              {postedAgo && <div className='mb-1'>Posted {postedAgo}</div>}
              {verticalLabel && (
                <div>
                  Looking for: <span>{verticalLabel}</span>
                </div>
              )}
            </div>

            {/* Job description */}
            {((booking as { message?: string })?.message || booking?.description) && (
              <p className='mb-4 whitespace-pre-line'>
                {(booking as { message?: string })?.message || booking?.description}
              </p>
            )}

            {/* Job includes + sweeteners */}
            {(includes.length > 0 || sweeteners.length > 0) && (
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-4'>
                {includes.length > 0 && (
                  <div>
                    <h4 className='font-semibold mb-2'>Job includes:</h4>
                    <ul className='list-disc pl-5 text-sm'>
                      {includes.map((inc) => (
                        <li key={inc.id}>{inc.label}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {sweeteners.length > 0 && (
                  <div>
                    <h4 className='font-semibold mb-2'>Deal sweeteners:</h4>
                    <ul className='list-disc pl-5 text-sm'>
                      {sweeteners.map((s) => (
                        <li key={s.id}>{s.label}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Flag content link — for sitters viewing a job */}
            <div className='mt-4'>
              <Button
                buttonType={ButtonType.tertiary}
                className='font-semibold text-sm'
                onClick={(): void => setFlagOpen(true)}
              >
                <i className='fa-regular fa-flag mr-1' />
                {t('pages.jobDetail.flagContent')}
              </Button>
            </div>
          </div>

          <ParentSummary parent={parent} minimized={!!isClosed} />
        </div>

        {/* ── Right column (1/3): interest sidebar + similar jobs ── */}
        <div className='md:col-span-1'>
          <div id='interest-card' className='us-card rounded p-5 mb-4 border border-neutral-200 shadow-sm bg-white'>
            {/* Rate + duration in sidebar header. Always renders so sitters
                see payment info even when booking data is partially populated. */}
            <div className='border-b border-neutral-200 pb-3 mb-3'>
              <div className='text-xs uppercase text-neutral-500 font-semibold mb-1'>
                {t('pages.jobDetail.rate')}
              </div>
              <div className='font-semibold text-xl'>
                {booking?.rate ? (
                  <>
                    ${booking.rate}
                    <span className='text-sm font-normal text-neutral-600'>/hr</span>
                  </>
                ) : (
                  <>
                    <span>Rate negotiable</span>
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      className='ml-1 text-sm text-neutral-500'
                      title={t('pages.jobDetail.minimumRateTooltip')}
                    />
                  </>
                )}
              </div>
              {booking?.rate && booking?.start && (
                <div className='text-sm text-neutral-600 mt-1'>
                  {getDurationString(booking.start, booking.end)}
                </div>
              )}
              {booking?.paymentType && (
                <div className='text-sm text-neutral-600 mt-1'>
                  Paid via {paymentLabel(booking.paymentType)}
                </div>
              )}
              {minimumHours != null && minimumHours > 0 && (
                <div className='text-sm text-neutral-600 mt-1'>
                  Guaranteed {minimumHours} hrs/week
                </div>
              )}
            </div>

            {job.lowNumberOfApplicants && (
              <div className='text-tangerine text-sm font-semibold mb-3'>
                <FontAwesomeIcon icon={faStar} className='mr-1' />
                Less than 10 applicants
              </div>
            )}

            {/* Application status */}
            {job.sitterInterest && (
              <div className='text-sm italic mb-3'>
                {applicationViewed ? (
                  <>
                    <i className='fa-regular fa-eye mr-2' />
                    {t('pages.jobInterest.applicationViewed')}
                  </>
                ) : (
                  <>
                    <i className='fa-regular fa-inbox-in mr-2' />
                    {t('pages.jobInterest.applicationReceived')}
                  </>
                )}
              </div>
            )}

            {isClosed && (
              <div className='text-sm italic mb-3 text-red-600'>
                <i className='fa-regular fa-circle-xmark mr-2' />
                {t('pages.jobInterest.closedStatus')}
              </div>
            )}

            {/* Not applied + not closed: notifications checkbox + apply button */}
            {!isClosed && !job.sitterInterest && (
              <>
                <label className='flex items-start gap-2 mb-4 text-sm cursor-pointer'>
                  <input
                    type='checkbox'
                    id='job-notifications'
                    checked={jobNotifications}
                    onChange={(e): void => setJobNotifications(e.target.checked)}
                    className='mt-1'
                  />
                  <span>{t('pages.jobInterest.jobNotifications')}</span>
                </label>
                {!jobIsHidden && (
                  <div className='mb-3'>
                    <Button
                      buttonType={ButtonType.primary}
                      onClick={apply}
                      disabled={isSubmitting}
                      className='w-full md:w-auto'
                    >
                      {isSubmitting ? (
                        <i className='fa-solid fa-spinner fa-spin mr-2' />
                      ) : (
                        <i className='fa-regular fa-pen-circle mr-2' />
                      )}
                      {t('pages.jobInterest.applyCta')}
                    </Button>
                  </div>
                )}
                <div className='text-sm'>
                  {!jobIsHidden ? (
                    <Button
                      buttonType={ButtonType.tertiary}
                      className='text-neutral-600'
                      onClick={hideJob}
                    >
                      <i className='fa-regular fa-eye-slash mr-1' />
                      {t('pages.jobDetail.hideJob')}
                    </Button>
                  ) : (
                    <Button
                      buttonType={ButtonType.tertiary}
                      className='text-neutral-600'
                      onClick={unhideJob}
                    >
                      <i className='fa-regular fa-eye mr-1' />
                      {t('pages.jobDetail.unhideJob')}
                    </Button>
                  )}
                </div>
              </>
            )}

            {/* Applied + not withdrawn + not closed: edit + withdraw */}
            {!isClosed && hasApplied && (
              <div className='flex flex-col gap-3'>
                <Button
                  buttonType={ButtonType.tertiary}
                  className='text-sapphire text-sm self-start'
                  onClick={(): void => openExpressInterestModal()}
                >
                  <i className='fa-regular fa-pen-to-square mr-2' />
                  {t('pages.jobDetail.editApplication')}
                </Button>
                <Button
                  buttonType={ButtonType.tertiary}
                  className='text-neutral-600 text-sm self-start'
                  onClick={withdraw}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <i className='fa-solid fa-spinner fa-spin mr-2' />
                  ) : (
                    <i className='fa-regular fa-trash-can mr-2' />
                  )}
                  {t('pages.jobInterest.withdrawCta')}
                </Button>
              </div>
            )}

            {/* Withdrawn + not closed: reapply */}
            {!isClosed && hasWithdrawn && (
              <Button
                buttonType={ButtonType.secondary}
                onClick={reapply}
                disabled={isSubmitting}
              >
                {isSubmitting && <i className='fa-solid fa-spinner fa-spin mr-2' />}
                {t('pages.jobDetail.reapply')}
              </Button>
            )}
          </div>

          {/* Similar jobs — always rendered so sitters can navigate to the
              full job board even when the similar-jobs endpoint returns
              nothing. */}
          <div className='us-card rounded p-4 border border-neutral-200 shadow-sm bg-white'>
            <h3 className='text-base font-semibold mb-3'>Similar jobs</h3>
            {similarJobs.length > 0 ? (
              <div className='flex flex-col gap-3'>
                {similarJobs.map((sj) => (
                  <div
                    key={sj.id}
                    role='button'
                    tabIndex={0}
                    onClick={(): void => navigate(`/job/${sj.id}`)}
                    onKeyDown={(e): void => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/job/${sj.id}`);
                      }
                    }}
                    className='flex items-start gap-3 p-2 cursor-pointer hover:bg-neutral-50 rounded'
                  >
                    {sj.booking?.parent?.pictureUrl && (
                      <img
                        src={sj.booking.parent.pictureUrl}
                        alt=''
                        className='w-14 h-14 rounded-full object-cover flex-shrink-0'
                      />
                    )}
                    <div className='flex-1 text-sm'>
                      <div className='font-semibold mb-1'>
                        {sj.recurring
                          ? `${sj.availability ?? ''}, Starting ${formatDateRange(sj.booking?.start)}`
                          : formatDateRange(sj.booking?.start, sj.booking?.end)}
                      </div>
                      {sj.booking?.rate ? (
                        <div className='text-neutral-600'>${sj.booking.rate}/hr</div>
                      ) : (
                        <div className='text-neutral-600'>Rate negotiable</div>
                      )}
                      {sj.booking?.address?.city && (
                        <div className='text-neutral-600'>{sj.booking.address.city}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className='text-sm text-neutral-600 italic'>
                No similar jobs right now — browse the full board for more options.
              </p>
            )}
            <div className='text-center mt-4'>
              <Button
                buttonType={ButtonType.tertiary}
                className='font-semibold text-sapphire'
                onClick={(): void => navigate('/job-board')}
              >
                See all available jobs <FontAwesomeIcon icon={faArrowRight} className='ml-1' />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom prev/next nav row + articles — sitter-only, mirrors Angular detail.tpl.html */}
      {!user?.parent && (
        <>
          {fromSearch && (
            <div className='hidden md:grid grid-cols-3 items-center font-semibold text-sm mt-2 mb-4'>
              <div>
                <Button
                  buttonType={ButtonType.tertiary}
                  disabled={!prevJobId}
                  onClick={(): void => { if (prevJobId) navigate(`/job/${prevJobId}`); }}
                  className={prevJobId ? '' : 'opacity-40 cursor-default'}
                >
                  <i className='fa-solid fa-chevron-left mr-1' /> Previous Job
                </Button>
              </div>
              <div className='text-center text-base'>
                <Button
                  buttonType={ButtonType.tertiary}
                  className='text-sapphire'
                  onClick={(): void => navigate(searchUrl)}
                >
                  {searchText}
                </Button>
              </div>
              <div className='text-right'>
                <Button
                  buttonType={ButtonType.tertiary}
                  disabled={!nextJobId}
                  onClick={(): void => { if (nextJobId) navigate(`/job/${nextJobId}`); }}
                  className={nextJobId ? '' : 'opacity-40 cursor-default'}
                >
                  Next Job <i className='fa-solid fa-chevron-right ml-1' />
                </Button>
              </div>
            </div>
          )}

          <hr className='my-6' />

          <div>
            <h3 className='text-base font-bold mb-4'>Articles and resources</h3>
            <div className='grid grid-cols-2 md:grid-cols-4 gap-5'>
              {ARTICLES.map((a) => (
                <a
                  key={a.id}
                  id={`article-${a.id}`}
                  href={a.url}
                  target='_blank'
                  rel='noreferrer'
                  className='block hover:underline'
                >
                  <img src={a.img} alt={a.alt} className='w-full h-auto rounded' />
                  <h4 className='font-bold mt-2 text-sm'>{a.title}</h4>
                </a>
              ))}
            </div>
          </div>
        </>
      )}

      <Modal
        isOpen={showModal}
        onClose={(): void => setShowModal(false)}
        title={
          modalStep === 'questions'
            ? t('pages.jobDetail.modalQuestionTitle', { name: parent?.firstName ?? '' })
            : t('pages.jobDetail.modalMessageTitle', { name: parent?.firstName ?? '' })
        }
        className='w-full max-w-lg'
        footer={
          modalStep === 'questions' ? (
            <>
              <Button buttonType={ButtonType.primary} onClick={sendAnswers}>
                {t('pages.jobDetail.modalSendAnswers')}
              </Button>
              <Button buttonType={ButtonType.tertiary} onClick={(): void => setModalStep('message')}>
                {t('pages.jobDetail.modalSkip')}
              </Button>
            </>
          ) : (
            <>
              <Button buttonType={ButtonType.primary} onClick={sendMessage}>
                {t('pages.jobDetail.modalSendMessage')}
              </Button>
              <Button buttonType={ButtonType.tertiary} onClick={(): void => setShowModal(false)}>
                {t('pages.jobDetail.modalSkip')}
              </Button>
            </>
          )
        }
      >
        {modalStep === 'questions' && (
          <div>
            {(job.questions ?? []).map((q) => (
              <div key={q.id} className='mb-4'>
                <p className='font-semibold text-sm mb-2 whitespace-pre-line'>{q.text}</p>
                <textarea
                  className='form-control w-full border border-neutral-300 rounded px-3 py-2 text-sm'
                  rows={5}
                  value={answers[q.id] ?? ''}
                  onChange={(e): void => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                />
              </div>
            ))}
          </div>
        )}
        {modalStep === 'message' && (
          <div>
            <p className='text-sm text-neutral-600 mb-3'>
              {t('pages.jobDetail.modalMessageIntro')}
            </p>
            <textarea
              aria-label={t('pages.jobDetail.modalMessageLabel')}
              className='form-control w-full border border-neutral-300 rounded px-3 py-2 text-sm'
              rows={5}
              placeholder={t('pages.jobDetail.modalMessagePlaceholder')}
              value={interestMessage}
              onChange={(e): void => setInterestMessage(e.target.value)}
            />
            <p className='text-xs text-neutral-500 mt-2 italic'>
              {t('pages.jobDetail.modalMessageDisclaimer')}
            </p>
          </div>
        )}
      </Modal>

      {/* Approval checklist modal */}
      <Modal
        isOpen={approvalChecklistOpen && !!profileStatus}
        onClose={(): void => setApprovalChecklistOpen(false)}
        title={t('pages.jobDetail.profileWaitingTitle')}
        className='max-w-lg w-full'
      >
        <p className='text-sm text-neutral-700 mb-4'>
          Complete the checklist to get approved, then you can apply for jobs.
        </p>
        {profileStatus && <ApprovalChecklist profileStatus={profileStatus} />}
      </Modal>

      {/* Generic apply-error modal */}
      <Modal
        isOpen={!!errorModal}
        onClose={(): void => setErrorModal(null)}
        title={errorModal?.header}
        className='max-w-lg w-full'
        footer={
          errorModal ? (
            <>
              {errorModal.secondary && (
                <Button
                  buttonType={ButtonType.tertiary}
                  onClick={errorModal.secondary.onClick}
                >
                  {errorModal.secondary.text}
                </Button>
              )}
              <Button
                buttonType={ButtonType.primary}
                onClick={
                  errorModal.primary?.onClick ?? ((): void => setErrorModal(null))
                }
              >
                {errorModal.primary?.text ?? 'Close'}
              </Button>
            </>
          ) : undefined
        }
      >
        {errorModal && (
          <>
            {errorModal.title && (
              <h3 className='text-base font-semibold mb-2'>{errorModal.title}</h3>
            )}
            {errorModal.body && (
              <p className='text-sm text-neutral-700 mb-4'>{errorModal.body}</p>
            )}
          </>
        )}
      </Modal>

      {/* Flag content modal */}
      {parent?.id && user?.id && (
        <FlagProfileModal
          isOpen={flagOpen}
          onClose={(): void => setFlagOpen(false)}
          flaggedId={parent.id}
          flaggingId={user.id}
        />
      )}
    </div>
  );
});
